<template>
  <MainHeader />
  <MainSidebar />
  <div class="main-content bg_gray d-flex flex-column transition overflow-hidden">
    <BreadcrumbMenu pageTitle="quotation_list" />

    <div class="card border-0 shadow-none rounded-1 mb-25">
      <div class="card-body p-xl-40">
        <div class="row g-3 align-items-center">
          <div class="col-lg-8">
            <div class="d-flex flex-wrap align-items-center gap-2">
              <button
                type="button"
                class="btn style-two px-4 h-55"
                data-bs-toggle="modal"
                data-bs-target="#quotationFilterModal"
              >
                {{ $t("quotation_page.filter") }}
                <i class="ri-filter-line ms-1"></i>
              </button>

              <div class="search-box position-relative">
                <input
                  type="text"
                  v-model="searchQuery"
                  :placeholder="$t('quotation_page.search_placeholder')"
                  class="form-control bg-white h-55 shadow-none pe-5"
                />
                <span class="search-icon">
                  <i class="ri-search-line"></i>
                </span>
              </div>
            </div>
          </div>

          <div class="col-lg-4">
            <div class="d-flex justify-content-lg-end">
              <router-link to="/create-quotation" class="btn style-one d-inline-flex align-items-center h-55 px-4">
                <i class="ri-add-circle-line me-1"></i>
                {{ $t("create_quotation") }}
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <QuotationList
      :quotations="quotations"
      :loading="loading"
      @view-details="handleViewDetails"
      @create-sale="handleCreateSale"
      @delete-quotation="handleDeleteQuotation"
    />

    <div class="flex-grow-1"></div>
    <MainFooter />
  </div>

  <QuotationDetails :quotation="selectedQuotation" />

  <div class="modal fade" id="quotationFilterModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content border-0 shadow">
        <div class="modal-header border-bottom-dashed">
          <h5 class="modal-title fw-bold">{{ $t("quotation_page.advanced_filter") }}</h5>
          <button type="button" class="btn-close shadow-none" data-bs-dismiss="modal" :aria-label="$t('common.close')"></button>
        </div>
        <div class="modal-body">
          <div class="row g-3">
            <div class="col-12">
              <label class="form-label fs-14 text-title">{{ $t("quotation_page.warehouse") }}</label>
              <select class="form-select bg-light border-0 fs-14 shadow-none" v-model="filters.warehouse_id">
                <option value="">{{ $t("quotation_page.all_warehouses") }}</option>
                <option v-for="w in warehouses" :key="w.id" :value="w.id">{{ w.name }}</option>
              </select>
            </div>
            <div class="col-12">
              <label class="form-label fs-14 text-title">{{ $t("quotation_page.status") }}</label>
              <select class="form-select bg-light border-0 fs-14 shadow-none" v-model="filters.status">
                <option value="">{{ $t("quotation_page.all_status") }}</option>
                <option value="pending">{{ $t("quotation_page.status_pending") }}</option>
                <option value="sent">{{ $t("quotation_page.status_sent") }}</option>
                <option value="completed">{{ $t("quotation_page.status_completed") }}</option>
              </select>
            </div>
          </div>
        </div>
        <div class="modal-footer border-top-dashed justify-content-between">
          <button type="button" class="btn btn-light" @click="resetFilters">{{ $t("quotation_page.reset") }}</button>
          <button type="button" class="btn btn-primary" data-bs-dismiss="modal" @click="applyFilters">{{ $t("quotation_page.apply_filter") }}</button>
        </div>
      </div>
    </div>
  </div>

  <div
    class="modal fade"
    :class="{ show: showConvertedModal }"
    id="convertedModal"
    tabindex="-1"
    aria-hidden="true"
    :style="showConvertedModal ? 'display: block; background: rgba(0,0,0,0.5)' : 'display: none'"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content border-0">
        <div class="modal-body p-4 text-center">
          <div class="mb-3">
            <img src="../../assets/img/icons/tick-circle.svg" :alt="$t('quotation_page.success')" width="60" />
          </div>
          <h4 class="mb-2">{{ $t("quotation_page.convert_sale_done") }}</h4>
          <p class="text-muted mb-4">{{ $t("quotation_page.already_converted_text") }}</p>
          <button type="button" class="btn btn-primary px-4" @click="showConvertedModal = false">{{ $t("common.close") }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import MainHeader from "../../components/Layouts/MainHeader.vue";
import MainSidebar from "../../components/Layouts/MainSidebar.vue";
import BreadcrumbMenu from "../../components/Common/BreadcrumbMenu.vue";
import QuotationList from "../../components/Quotations/QuotationList/QuotationList.vue";
import QuotationDetails from "../../components/Quotations/QuotationList/QuotationDetails.vue";
import MainFooter from "../../components/Layouts/MainFooter.vue";
import { useQuotationList } from "../../composables/useQuotation";

export default defineComponent({
  name: "QuotationListPage",
  components: {
    MainHeader,
    MainSidebar,
    BreadcrumbMenu,
    QuotationList,
    QuotationDetails,
    MainFooter,
  },
  setup() {
    return useQuotationList();
  },
});
</script>

<style scoped>
.search-box {
  width: min(100%, 360px);
}

.search-box .form-control {
  padding-left: 18px;
  border: 1px solid #e6ebf2;
  border-radius: 8px;
  background: #fff;
  color: #1f2940;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.04);
}

.search-box .form-control:focus {
  border-color: #5d5fef;
  box-shadow: 0 0 0 3px rgba(93, 95, 239, 0.12);
}

.search-icon {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #8f9bb3;
  pointer-events: none;
  font-size: 18px;
}
</style>
